import React, { useCallback, useEffect, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ClockIcon, BanknotesIcon, DocumentTextIcon, SwatchIcon, ShieldCheckIcon, CurrencyDollarIcon, ChartBarIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline';
import { useBillingStore } from '../store/billingStore';
import { useBilling } from '../hooks/useBilling';
import { useAnimatedNavigate } from '../../../hooks/useAnimatedNavigate';
import BillingKpiSummaryBar from '../components/BillingKpiSummaryBar';
import BillingControlsBar from '../components/BillingControlsBar';
import TimeEntriesView from '../components/TimeEntriesView';
import ExpensesView from '../components/ExpensesView';
import InvoicesView from '../components/InvoicesView';
import TimeEntryDrawer from '../components/TimeEntryDrawer';
import ExpenseDrawer from '../components/ExpenseDrawer';
import BillDrawer from '../components/BillDrawer';
import InvoiceDetailPage from './InvoiceDetailPage';
import CreateInvoicePage from './CreateInvoicePage';
import ThemeEditorPage from './ThemeEditorPage';
import ThemeListPage from '../components/themes/ThemeListPage';
import TrustAccountsPage from './TrustAccountsPage';
import TrustAccountDetailPage from './TrustAccountDetailPage';
import TrustReconciliationWizard from './TrustReconciliationWizard';
import TrustReportsPage from './TrustReportsPage';
import RateSchedulesPage from './RateSchedulesPage';
import BillingReportsPage from './BillingReportsPage';
import GuidelinesPage from './GuidelinesPage';

/** Main billing list view (time entries, expenses, invoices tabs) */
const BillingDashboardContent: React.FC = () => {
  const navigate = useAnimatedNavigate();
  const store = useBillingStore();
  const {
    fetchTimeEntries,
    fetchTimeEntryStats,
    fetchExpenses,
    fetchExpenseStats,
    fetchBills,
    fetchBillStats,
    getPaymentStatistics,
    loading,
    error,
  } = useBilling();
  const [paymentStats, setPaymentStats] = useState<import('../types/billing.types').PaymentStatistics | null>(null);

  // Initial data load
  useEffect(() => {
    fetchTimeEntryStats().catch(() => {});
    fetchExpenseStats().catch(() => {});
    fetchBillStats().catch(() => {});
    getPaymentStatistics().then(setPaymentStats).catch(() => {});
  }, []);

  // Fetch data when tab changes
  useEffect(() => {
    if (store.activeTab === 'time-entries') {
      fetchTimeEntries().catch(() => {});
    } else if (store.activeTab === 'expenses') {
      fetchExpenses().catch(() => {});
    } else if (store.activeTab === 'invoices') {
      fetchBills().catch(() => {});
    }
  }, [store.activeTab]);

  // Refresh handler for controls bar filter changes
  const handleRefresh = useCallback(() => {
    if (store.activeTab === 'time-entries') {
      fetchTimeEntries().catch(() => {});
    } else if (store.activeTab === 'expenses') {
      fetchExpenses().catch(() => {});
    } else if (store.activeTab === 'invoices') {
      fetchBills().catch(() => {});
    }
  }, [store.activeTab, fetchTimeEntries, fetchExpenses, fetchBills]);

  return (
    <div className="py-2 space-y-6" style={{ viewTransitionName: 'billing-page' }}>
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" style={{ viewTransitionName: 'billing-title' }}>Billing</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage time entries, expenses, and billing workflows.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/billing/trust-accounts')}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-teal-600 dark:text-teal-400 border border-teal-300 dark:border-teal-600 rounded-lg hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors"
          >
            <ShieldCheckIcon className="h-4 w-4" />
            Trust Accounts
          </button>
          <button
            onClick={() => navigate('/billing/rate-schedules')}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-amber-600 dark:text-amber-400 border border-amber-300 dark:border-amber-600 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
          >
            <CurrencyDollarIcon className="h-4 w-4" />
            Rate Schedules
          </button>
          <button
            onClick={() => navigate('/billing/reports')}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-lex-accent border border-lex-accent rounded-lg hover:bg-lex-state-hover transition-colors"
          >
            <ChartBarIcon className="h-4 w-4" />
            Reports
          </button>
          <button
            onClick={() => navigate('/billing/guidelines')}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-rose-600 dark:text-rose-400 border border-rose-300 dark:border-rose-600 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
          >
            <ShieldExclamationIcon className="h-4 w-4" />
            Guidelines
          </button>
          <button
            onClick={() => navigate('/billing/themes')}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <SwatchIcon className="h-4 w-4" />
            Invoice Themes
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* KPI Summary */}
      <BillingKpiSummaryBar
        timeStats={store.timeEntryStats}
        expenseStats={store.expenseStats}
        paymentStats={paymentStats}
      />

      {/* Tab navigation */}
      <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => store.setActiveTab('time-entries')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            store.activeTab === 'time-entries'
              ? 'border-lex-accent text-lex-accent'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <ClockIcon className="h-4 w-4" />
          Time Entries
        </button>
        <button
          onClick={() => store.setActiveTab('expenses')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            store.activeTab === 'expenses'
              ? 'border-lex-accent text-lex-accent'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <BanknotesIcon className="h-4 w-4" />
          Expenses
        </button>
        <button
          onClick={() => store.setActiveTab('invoices')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            store.activeTab === 'invoices'
              ? 'border-lex-accent text-lex-accent'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <DocumentTextIcon className="h-4 w-4" />
          Invoices
        </button>
      </div>

      {/* Controls bar */}
      <BillingControlsBar activeTab={store.activeTab} onRefresh={handleRefresh} />

      {/* Tab content — always mounted to prevent unmount/remount jank */}
      <div className="relative">
        {loading && (
          <div className="absolute inset-x-0 top-0 flex items-center justify-center py-2 z-10">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-full shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="h-4 w-4 border-2 border-lex-accent border-t-transparent rounded-full animate-spin" />
              <span className="text-xs text-gray-500 dark:text-gray-400">Loading...</span>
            </div>
          </div>
        )}
        <div key={store.activeTab} className="lex-tab-panel">
          {store.activeTab === 'time-entries' && <TimeEntriesView />}
          {store.activeTab === 'expenses' && <ExpensesView />}
          {store.activeTab === 'invoices' && <InvoicesView />}
        </div>
      </div>

      {/* Drawers */}
      {store.drawerOpen && store.drawerType === 'time-entry' && <TimeEntryDrawer />}
      {store.drawerOpen && store.drawerType === 'expense' && <ExpenseDrawer />}
      {store.drawerOpen && store.drawerType === 'bill' && <BillDrawer />}
    </div>
  );
};

/** Top-level billing component with nested routes */
const BillingDashboard: React.FC = () => {
  return (
    <Routes>
      <Route index element={<BillingDashboardContent />} />
      <Route path="create-invoice" element={<CreateInvoicePage />} />
      <Route path="invoices/:billId" element={<InvoiceDetailPage />} />
      <Route path="themes" element={<ThemeListPage />} />
      <Route path="themes/new" element={<ThemeEditorPage />} />
      <Route path="themes/:themeId/edit" element={<ThemeEditorPage />} />
      <Route path="trust-accounts" element={<TrustAccountsPage />} />
      <Route path="trust-accounts/:accountId" element={<TrustAccountDetailPage />} />
      <Route path="trust-accounts/:accountId/reconcile" element={<TrustReconciliationWizard />} />
      <Route path="trust-reports" element={<TrustReportsPage />} />
      <Route path="rate-schedules" element={<RateSchedulesPage />} />
      <Route path="reports" element={<BillingReportsPage />} />
      <Route path="guidelines" element={<GuidelinesPage />} />
    </Routes>
  );
};

export default BillingDashboard;
